<template>
  <LayoutPage
    :title="$t('jobPlatform.listHeading')"
    :subtitle="$t('jobPlatform.listInfo')"
  >
    <div
      class="grid gap-8 md:gap-10 col-span-12 lg:col-span-7 xl:col-span-8 content-start"
    >
      <JobFilterBar
        v-if="isSite('altinget_dk') && allJobListData"
        :activeFilters="filterValues"
        :filterOptions="{
          category: allJobListData.categoryKeyList,
          location: allJobListData.locationKeyList,
        }"
        @setChange="setChange"
      />
      <div class="grid gap-3.5 md:gap-5">
        <JobFilterList
          v-if="allJobListData"
          :activeTotalList="allJobListData.dataList"
          :activeFilters="activeFilters"
          :placement="placementId"
        />
      </div>
      <div
        v-if="!isMm()"
        class="md:order-first flex gap-5 items-center flex-col md:flex-row md:justify-between border border-gray-300 rounded p-4 md:p-8"
      >
        <div>
          <h2 class="text-sm md:text-xl font-sans font-bold">
            {{ $t('jobPlatform.forCompanies') }}
          </h2>
          <p class="text-sm md:text-base mt-1">
            {{ $t('jobPlatform.forCompaniesInfo') }}
          </p>
        </div>
        <NuxtLink :to="{ name: 'ads' }">
          <CommonButton
            class="text-lg flex gap-2"
            bg="bg-blue"
            text="text-white"
            size="large"
          >
            <span>{{ $t('jobPlatform.CTAforCompanies') }}</span>
            <CommonFontAwesomeIcon icon="fa-regular fa-arrow-right" />
          </CommonButton>
        </NuxtLink>
      </div>
    </div>
    <div
      class="flex flex-col gap-8 md:gap-10 col-span-12 lg:col-span-4 lg:col-start-9 xl:col-span-3 xl:col-start-10 h-min sticky top-20"
    >
      <div v-if="isSite('altinget_dk')">
        <CommonCommercialPartnerGenitor />
        <template v-if="partnerJobData && partnerJobData.length > 0">
          <ListJobs
            :jobs="partnerJobData"
            :count="3"
            :placement="placementId"
            :has-fixed-top="false"
            :label="false"
          />
        </template>
      </div>
      <div v-if="promotedJobs.length">
        <h2 class="text-sm md:text-xl font-sans font-bold">
          {{ $t('jobPlatform.promotedJobs') }}
        </h2>
        <div class="mt-3 md:mt-6 border-t border-gray-300">
          <JobTeaser
            v-for="job in promotedJobs"
            :job="job"
            :key="job.id"
            :placement="placementId"
          />
        </div>
      </div>
    </div>
  </LayoutPage>
</template>

<script setup lang="ts">
import { tagTypes, GENITOR_PARTNER_ID } from '~/api/jobs'
import escapeStringRegexp from 'escape-string-regexp'

import type {
  JobSearchAndData,
  FilterChangeEvent,
  ActiveJobFilter,
} from '~/typesManual/jobTypes'

const router = useRouter()
const route = useRoute()
const nuxtApp = useNuxtApp()
const config = useRuntimeConfig()

const placements = config.public.site.job?.placements
const placementId = placements?.jobPage ?? placements?.default

type JobListData = {
  dataList: JobSearchAndData[]
  categoryKeyList: string[]
  locationKeyList: string[]
}

const { data: partnerJobData } = await useAsyncData(async () => {
  if (!isSite('altinget_dk')) {
    return []
  }

  const rawResponse = await nuxtApp.$api.jobs.listJobAds({
    placementId: placementId,
    pageSize: 100,
    partnerId: GENITOR_PARTNER_ID,
  })

  // Remove items without slug from the list.
  return rawResponse.data?.filter((item) => item.slug)
})

const { data: allJobListData } = await useAsyncData(async () => {
  const rawResponse = await nuxtApp.$api.jobs.listJobAds({
    placementId: placementId,
    pageSize: 100,
  })

  const dataList: JobSearchAndData[] = []
  const categoryFilterMap: { [id: string]: boolean } = {}
  const locationFilterMap: { [id: string]: boolean } = {}

  // Remove items without slug from the list.
  const rawDataList = rawResponse.data?.filter((item) => item.slug)

  if (rawDataList) {
    for (let i = 0; i < rawDataList.length; i++) {
      const rawDataItem = rawDataList[i]

      if (rawDataItem === null || rawDataItem === undefined) {
        continue
      }

      const textTokens = []
      if (rawDataItem.title) {
        textTokens.push(...rawDataItem.title.split(' '))
      }

      const categoryTokens: string[] = []
      if (rawDataItem.jobAdCategories) {
        for (let i = 0; i < rawDataItem.jobAdCategories.length; i++) {
          const categoryDto = rawDataItem.jobAdCategories[i]

          if (categoryDto.category && categoryDto.category.name) {
            if (categoryFilterMap[categoryDto.category.name] === undefined) {
              categoryFilterMap[categoryDto.category.name] = true
            }
            categoryTokens.push(categoryDto.category.name)
          }
        }
      }

      const locationTokens: string[] = []
      if (rawDataItem.location && rawDataItem.location.name) {
        locationTokens.push(rawDataItem.location.name)
        if (locationFilterMap[rawDataItem.location.name] === undefined) {
          locationFilterMap[rawDataItem.location.name] = true
        }
      }

      dataList.push({
        searchable: {
          text: textTokens,
          category: categoryTokens,
          location: locationTokens,
          date: rawDataItem.onlineTime
            ? new Date(rawDataItem.onlineTime)
            : new Date(Date.UTC(1900, 0, 1)),
        },
        data: rawDataItem,
      })
    }
  }

  const categoryKeyList = Object.keys(categoryFilterMap)
  categoryKeyList.sort()
  const locationKeyList = Object.keys(locationFilterMap)
  locationKeyList.sort()

  const returnData: JobListData = { dataList, categoryKeyList, locationKeyList }
  return returnData
})

type FilterValues = {
  searchString?: string
  location?: string[]
  category?: string[]
}
const filterValues = computed(() => {
  const queryDict: FilterValues = {}
  if (route.query.search) {
    queryDict['searchString'] = route.query.search.toString()
  }

  if (route.query.location) {
    queryDict['location'] = route.query.location.toString().split(';')
  }

  if (route.query.category) {
    queryDict['category'] = route.query.category.toString().split(';')
  }

  return queryDict
})

const setChange = (filterChanges: FilterChangeEvent) => {
  const urlDict: { [id: string]: string } = {}
  if (filterChanges.searchString !== '') {
    urlDict['search'] = filterChanges.searchString
  }
  if (filterChanges.location.length > 0) {
    urlDict['location'] = filterChanges.location.join(';')
  }
  if (filterChanges.category.length > 0) {
    urlDict['category'] = filterChanges.category.join(';')
  }
  router.replace({ query: urlDict })
}

const activeFilters = computed(() => {
  const newActiveFilters: ActiveJobFilter = {
    setup: {
      text: 'AND',
      location: 'OR',
      category: 'OR',
    },
  }

  if (
    filterValues.value.searchString &&
    filterValues.value.searchString !== ''
  ) {
    newActiveFilters['text'] = filterValues.value.searchString
      .split(' ')
      .map((token) => {
        return new RegExp(`.*${escapeStringRegexp(token)}.*`, 'i')
      })
  }
  if (filterValues.value.location && filterValues.value.location.length > 0) {
    newActiveFilters['location'] = filterValues.value.location.map((label) => {
      return new RegExp(`^${escapeStringRegexp(label)}$`, 'i')
    })
  }
  if (filterValues.value.category && filterValues.value.category.length > 0) {
    newActiveFilters['category'] = filterValues.value.category.map((label) => {
      return new RegExp(`^${escapeStringRegexp(label)}$`, 'i')
    })
  }

  return newActiveFilters
})

const promotedJobs = computed(() => {
  const promotedJobsList = []

  if (allJobListData.value === null) {
    return []
  }

  const jobSearchableList = allJobListData.value.dataList
  for (let i = 0; i < jobSearchableList.length; i++) {
    const itemData = jobSearchableList[i].data

    if (itemData.jobAdTags) {
      for (let j = 0; j < itemData.jobAdTags.length; j++) {
        const element = itemData.jobAdTags[j]

        if (element.tagId === tagTypes.promotedJob) {
          promotedJobsList.push(itemData)
        }
      }
    }
  }

  return promotedJobsList
})

useHead(() => {
  return {
    title: `${config.public.site.meta.title.postfix.short} ${nuxtApp.$t(
      'jobPlatform.listHeading'
    )}`,
  }
})
</script>
